import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent, isCart } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    if (productId === 'cart' || isCart === 'true') {
      this.#loadCartRecommendations(sectionId, intent);
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (result.data?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          morphSection(sectionId, result.data, 'hydration', { injectStylesheet: true });
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Load recommendations for all products in the cart
   * @param {string} sectionId
   * @param {string} intent
   */
  async #loadCartRecommendations(sectionId, intent) {
    try {
      const cart = await fetch(`${window.Shopify.routes.root}cart.js`).then((res) => res.json());
      if (!cart.items || cart.items.length === 0) {
        this.classList.add('hidden');
        return;
      }

      const productIds = [...new Set(cart.items.map((item) => item.product_id))].reverse();
      const seenIds = new Set(cart.items.map((item) => item.product_id));
      const limit = parseInt(this.querySelector('.resource-list')?.getAttribute('data-max-products') || '4') || 4;
      
      let finalHtml = '';
      let cardsAdded = 0;

      for (const id of productIds) {
        if (cardsAdded >= limit) break;

        const result = await this.#fetchCachedRecommendations(id, sectionId, intent);
        if (!result.success || !result.data) continue;

        const parser = new DOMParser();
        const doc = parser.parseFromString(result.data, 'text/html');
        // Find the custom element or the resource-list in the response
        const newItems = doc.querySelectorAll('.resource-list__item');

        if (newItems.length > 0) {
          if (!finalHtml) {
            // Initialize finalHtml with the full first response
            const container = doc.querySelector('.resource-list');
            if (container) {
              container.innerHTML = ''; // Clear and fill with unique cards
              for (const item of newItems) {
                const pIdStr = item.getAttribute('data-product-id') || item.querySelector('[data-product-id]')?.getAttribute('data-product-id');
                const pId = pIdStr ? Number(pIdStr) : null;
                if (pId && !seenIds.has(pId)) {
                  seenIds.add(pId);
                  container.appendChild(item);
                  cardsAdded++;
                  if (cardsAdded >= limit) break;
                }
              }
              // Ensure the root element in the doc is visible and marked as performed
              const root = doc.querySelector('product-recommendations');
              if (root) {
                root.classList.remove('hidden');
                root.setAttribute('data-recommendations-performed', 'true');
              }

              const header = doc.querySelector('.section-resource-list__header');
              if (header && !header.textContent?.trim() && this.dataset.isCart === 'true') {
                header.innerHTML = `<h3>${this.dataset.cartTitle}</h3>`;
              }

              finalHtml = doc.body.innerHTML;
            }
          } else {
            // Append more unique cards to the already initialized finalHtml
            const tempDoc = parser.parseFromString(finalHtml, 'text/html');
            const container = tempDoc.querySelector('.resource-list');
            if (container) {
              for (const item of newItems) {
                const pId = Number(item.dataset.productId || item.querySelector('[data-product-id]')?.dataset.productId);
                if (pId && !seenIds.has(pId)) {
                  seenIds.add(pId);
                  container.appendChild(item);
                  cardsAdded++;
                  if (cardsAdded >= limit) break;
                }
              }
              finalHtml = tempDoc.body.innerHTML;
            }
          }
        }
      }

      if (cardsAdded > 0) {
        this.dataset.recommendationsPerformed = 'true';
        this.classList.remove('hidden');
        
        // Final guard: Ensure the HTML being injected doesn't have the hidden class on its root
        const parser = new DOMParser();
        const finalDoc = parser.parseFromString(finalHtml, 'text/html');
        const finalRoot = finalDoc.querySelector('product-recommendations');
        if (finalRoot) {
          finalRoot.classList.remove('hidden');
          finalRoot.dataset.recommendationsPerformed = 'true';
          finalHtml = finalDoc.body.innerHTML;
        }

        morphSection(sectionId, finalHtml, 'hydration', { injectStylesheet: true });
        
        // Force visibility one more time after morphing in case the renderer preserved the old class
        this.classList.remove('hidden');
      } else {
        this.classList.add('hidden');
      }
    } catch (e) {
      this.#handleError(e);
    }
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
